import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import {
  switchMap,
  map,
  catchError,
  withLatestFrom,
  filter,
  debounceTime
} from 'rxjs/operators';
import {
  FavoriteOccupationsService,
  OccupationService
} from '@cpd-app/core-module/services';
import { FavoriteMilitaryOccupation } from '@cpd-app/generated';

import * as FavoriteMilitaryOccupationsActions from './favorite-occupations.actions';
import { ErrorReceived } from '../error/error.actions';
import { UserProfileActions, UserProfileFacade } from '../user-profile';
import { AuthFacade } from '../auth/auth.facade';
import { untilSessionToken } from '../auth/until-session-token';

@Injectable()
export class FavoriteMilitaryOccupationsEffects {
  private readonly authFacade = inject(AuthFacade);

  public loadFavoriteMilitaryOccupations$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        UserProfileActions.userProfileLoadedSuccess,
        UserProfileActions.saveUserProfileToState
      ),
      debounceTime(500),
      switchMap(() =>
        this.favoriteOccupationsService.getFavoriteMilitaryOccupations().pipe(
          map((favoriteMilitaryOccupations) =>
            FavoriteMilitaryOccupationsActions.favoriteMilitaryOccupationsLoadedSuccess(
              {
                favoriteMilitaryOccupations
              }
            )
          ),
          catchError((error) => of(ErrorReceived({ error })))
        )
      )
    );
  });

  public addMilitaryFavoriteOccupation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FavoriteMilitaryOccupationsActions.addMilitaryFavoriteOccupation),
      switchMap(({ mocCode, mocTitle }) =>
        this.favoriteOccupationsService
          .addFavoritemilitaryOccupation(mocCode, mocTitle)
          .pipe(
            map((favoriteMilitaryOccupation) =>
              FavoriteMilitaryOccupationsActions.favoriteMilitaryOccupationAddedSuccess(
                {
                  favoriteMilitaryOccupation
                }
              )
            ),
            catchError((error) => of(ErrorReceived({ error })))
          )
      )
    );
  });

  public removeMilitaryFavoriteOccupation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        FavoriteMilitaryOccupationsActions.removeMilitaryFavoriteOccupation
      ),
      switchMap(({ mocCode, mocTitle }) =>
        this.favoriteOccupationsService
          .removeFavoriteMilitaryOccupation(mocCode, mocTitle)
          .pipe(
            map(() =>
              FavoriteMilitaryOccupationsActions.favoriteMilitaryOccupationRemovedSuccess(
                {
                  mocCode,
                  mocTitle
                }
              )
            ),
            catchError((error) => of(ErrorReceived({ error })))
          )
      )
    );
  });

  loadSimilarMilitaryOccupations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FavoriteMilitaryOccupationsActions.loadSimilarMilitaryOccupations),
      withLatestFrom(this.userProfileFacade.userProfileLoadIfEmpty$),
      filter(([_, userProfile]) => userProfile?.isSkillsSurveyComplete),
      // Release scan, 1 Oct. Fired by the occupation resolver at the same
      // moment as the detail read, so on a cold load it too can go out before
      // the session token and come back Forbidden. Same wait as the detail.
      switchMap(([action]) =>
        untilSessionToken(this.authFacade.authToken$, 3000)
          .pipe(
            switchMap(() =>
              this.occupationService.getSimilarMilitaryOccupations(
                action.onetCode,
                action.search
              )
            )
          )
          .pipe(
            map((occupations: FavoriteMilitaryOccupation[]) =>
              occupations
                ? FavoriteMilitaryOccupationsActions.loadSimilarMilitaryOccupationsSuccess(
                    {
                      occupations
                    }
                  )
                : FavoriteMilitaryOccupationsActions.loadSimilarMilitaryOccupationsFailed()
            ),
            catchError((error: TypeError) => of(ErrorReceived({ error })))
          )
      )
    )
  );

  loadMilitaryOccupationsForGoal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FavoriteMilitaryOccupationsActions.loadMilitaryOccupationsForGoal),
      withLatestFrom(this.userProfileFacade.userProfileLoadIfEmpty$),
      filter(([_, userProfile]) => userProfile?.isSkillsSurveyComplete),
      switchMap(([action]) =>
        this.occupationService
          .getMilitaryOccupationsForGoal(action.search)
          .pipe(
            map((occupations: FavoriteMilitaryOccupation[]) =>
              occupations
                ? FavoriteMilitaryOccupationsActions.loadMilitaryOccupationsForGoalSuccess(
                    {
                      occupations
                    }
                  )
                : FavoriteMilitaryOccupationsActions.loadMilitaryOccupationsForGoalFailed()
            ),
            // WALK-71 review: a failure ends the loading state as well.
            catchError((error: TypeError) =>
              of(
                ErrorReceived({ error }),
                FavoriteMilitaryOccupationsActions.loadMilitaryOccupationsForGoalFailed()
              )
            )
          )
      )
    )
  );

  updateReclassifying$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FavoriteMilitaryOccupationsActions.updateReclassifying),
      switchMap(({ transitionfrommilitary, stayincurrent }) =>
        this.favoriteOccupationsService
          .updateReclasyfying(transitionfrommilitary, stayincurrent)
          .pipe(
            map((userReclassifying) =>
              FavoriteMilitaryOccupationsActions.updateReclassifyingSuccess({
                userReclassifying
              })
            ),
            catchError((error) => of(ErrorReceived({ error })))
          )
      )
    )
  );

  loadUserReclassifying$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.saveUserProfileToState),
      switchMap(() =>
        this.favoriteOccupationsService.userReclassifying().pipe(
          map((userReclassifying) =>
            FavoriteMilitaryOccupationsActions.userReclassifyingSuccess({
              userReclassifying
            })
          ),
          catchError((error) => of(ErrorReceived({ error })))
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private favoriteOccupationsService: FavoriteOccupationsService,
    private userProfileFacade: UserProfileFacade,
    private occupationService: OccupationService
  ) {}
}
